import { useState } from "react";
import { X, ChevronDown, Trophy, Coins, Gift } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { api, formatApiError } from "@/lib/api";

export default function AuthModal({ onClose, initialMode = "register" }) {
  const { t } = useLang();
  const { login, register } = useAuth();

  // Sign up (primary)
  const [username, setUsername] = useState("");
  const [age, setAge] = useState("");
  const [suEmail, setSuEmail] = useState("");
  const [suPassword, setSuPassword] = useState("");
  const [suErr, setSuErr] = useState("");
  const [suBusy, setSuBusy] = useState(false);

  // Sign in (collapsible)
  const [showSignIn, setShowSignIn] = useState(initialMode === "login");
  const [siEmail, setSiEmail] = useState("");
  const [siPassword, setSiPassword] = useState("");
  const [siErr, setSiErr] = useState("");
  const [siBusy, setSiBusy] = useState(false);

  // Lead capture (always visible)
  const [leadEmail, setLeadEmail] = useState("");
  const [leadBusy, setLeadBusy] = useState(false);

  const signUp = async () => {
    setSuErr(""); setSuBusy(true);
    try {
      const ageNum = parseInt(age, 10);
      if (!ageNum || ageNum < 1 || ageNum > 120) { setSuErr(t("ageError") || "Please enter a valid age."); setSuBusy(false); return; }
      await register(suEmail, suPassword, username, ageNum);
      onClose();
    } catch (e) {
      setSuErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSuBusy(false); }
  };

  const signIn = async () => {
    setSiErr(""); setSiBusy(true);
    try {
      await login(siEmail, siPassword);
      onClose();
    } catch (e) {
      setSiErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSiBusy(false); }
  };

  const submitLead = async () => {
    if (!leadEmail.trim()) return;
    setLeadBusy(true);
    try {
      await api.captureLead({ email: leadEmail.trim(), source: "auth_modal" });
      toast.success(t("leadCaptureThanks"));
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLeadBusy(false); }
  };

  const inputCls = "font-pixel text-sm bg-white text-black rounded-xl px-4 py-3 outline-none w-full";

  return (
    <div className="absolute inset-0 z-[140] overflow-y-auto overscroll-contain flex items-start justify-center bg-black/85 p-4 scanlines" data-testid="auth-modal">
      <div className="pixel-card bg-[#141414] p-6 md:p-8 w-full max-w-sm pop-in my-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-pixel text-lg text-[#FFEB3B] pixel-stroke">{t("createProfileTitle", "Create Your Profile")}</h2>
          <button data-testid="auth-close-btn" onClick={onClose} className="pixel-btn bg-black/55 text-white p-2"><X size={16} strokeWidth={2.5} /></button>
        </div>

        {/* Why create a profile — drives sign-ups */}
        <div className="flex items-center justify-center gap-4 mb-4 text-white/85" data-testid="profile-benefits">
          <span className="flex flex-col items-center gap-1 font-vt text-sm leading-none"><Trophy size={20} className="text-[#FFB300]" /> {t("benefitScores", "Save scores")}</span>
          <span className="flex flex-col items-center gap-1 font-vt text-sm leading-none"><Coins size={20} className="text-[#FFB300]" /> {t("benefitCoins", "Earn coins")}</span>
          <span className="flex flex-col items-center gap-1 font-vt text-sm leading-none"><Gift size={20} className="text-[#4CAF50]" /> {t("benefitGifts", "Free gifts")}</span>
        </div>

        {/* Sign Up (primary) */}
        <div className="flex flex-col gap-3">
          <input data-testid="auth-username" value={username} onChange={(e) => setUsername(e.target.value)} maxLength={20}
            placeholder={t("username")} className={inputCls} />
          <input data-testid="auth-age" value={age} onChange={(e) => setAge(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))} type="number" min={1} max={120} inputMode="numeric"
            placeholder={t("age")} className={inputCls} />
          <input data-testid="auth-email" value={suEmail} onChange={(e) => setSuEmail(e.target.value)} type="email"
            placeholder={t("email")} className={inputCls} />
          <input data-testid="auth-password" value={suPassword} onChange={(e) => setSuPassword(e.target.value)} type="password"
            placeholder={t("password")} className={inputCls} />
          {suErr && <p className="font-vt text-lg text-[#ff5252]" data-testid="auth-error">{suErr}</p>}
          <button data-testid="auth-submit-btn" onClick={signUp} disabled={suBusy} className="pixel-btn bg-[#4CAF50] text-white px-6 py-3 text-xs">
            {suBusy ? "..." : t("createProfileBtn", "Create Profile")}
          </button>
        </div>

        {/* Collapsible Sign In (returning users) */}
        <div className="border-t border-white/10 pt-3 mt-4">
          <button
            data-testid="signin-toggle-btn"
            onClick={() => setShowSignIn((v) => !v)}
            className="w-full flex items-center justify-between font-vt text-lg text-white/80 hover:text-white transition-colors"
            aria-expanded={showSignIn}
          >
            <span data-testid="signin-heading">{t("haveAccount")}</span>
            <ChevronDown size={18} strokeWidth={2.5} className={`transition-transform ${showSignIn ? "rotate-180" : ""}`} />
          </button>
          {showSignIn && (
            <div className="flex flex-col gap-2.5 mt-3 pop-in" data-testid="signin-panel">
              <input data-testid="signin-email" value={siEmail} onChange={(e) => setSiEmail(e.target.value)} type="email"
                placeholder={t("email")} className="font-pixel text-xs bg-white text-black rounded-xl px-3 py-2.5 outline-none w-full" />
              <input data-testid="signin-password" value={siPassword} onChange={(e) => setSiPassword(e.target.value)} type="password"
                placeholder={t("password")} className="font-pixel text-xs bg-white text-black rounded-xl px-3 py-2.5 outline-none w-full" />
              {siErr && <p className="font-vt text-base text-[#ff5252]" data-testid="signin-error">{siErr}</p>}
              <button data-testid="signin-submit-btn" onClick={signIn} disabled={siBusy}
                className="pixel-btn bg-[#00897B] text-white px-4 py-2.5 text-[11px]">{siBusy ? "..." : t("signIn")}</button>
            </div>
          )}
        </div>

        {/* Lead capture — always visible so it reliably captures emails */}
        <div className="border-t border-white/10 pt-3 mt-3" data-testid="lead-section">
          <p className="font-vt text-base text-white/70 mb-2">{t("leadCapturePrompt")}</p>
          <div className="flex gap-2" data-testid="lead-panel">
            <input data-testid="lead-email" value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)} type="email"
              placeholder={t("email")} className="flex-1 min-w-0 font-pixel text-xs bg-white text-black rounded-xl px-3 py-2.5 outline-none" />
            <button data-testid="lead-submit-btn" onClick={submitLead} disabled={leadBusy}
              className="pixel-btn bg-[#4CAF50] text-white px-4 py-2.5 text-[10px] shrink-0">{leadBusy ? "..." : t("leadCaptureBtn")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
